import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ClientInfo, BrowserInfo, DeviceInfo, DeviceType } from '../models/client-info.model';

@Injectable({ providedIn: 'root' })
export class DeviceDetectionService {

    private readonly platformId = inject(PLATFORM_ID);
    private cached?: ClientInfo;

    isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }


    public isInApp(): boolean {
        if (!this.isBrowser()) return false;
        const ua = navigator.userAgent || '';
        return this.detectInAppBrowser(ua);
    }


    async detect(): Promise<ClientInfo> {
        if (!this.isBrowser()) {
            // SSR-safe fallback
            this.cached = {
                browser: { name: 'Server', mobile: false },
                device: { type: 'unknown', touch: false },
                locale: undefined,
                platform: 'node',
                timestamp: new Date().toISOString(),
            };
            return this.cached;
        }

        // Try UA-CH first (modern)


        // With this (safe for TS and SSR):
        type UAData = {
            brands: { brand: string; version: string }[];
            mobile?: boolean;
            getHighEntropyValues?: (hints: string[]) => Promise<{
                brands?: { brand: string; version: string }[];
                platform?: string;
                platformVersion?: string;
                model?: string;
                architecture?: string;
                bitness?: string;
                mobile?: boolean;
            }>;
        };

        const nav = navigator as any;
        const hasUACh = !!nav.userAgentData;

        let browser: BrowserInfo = { name: 'Unknown', mobile: false };
        let device: DeviceInfo = {
            type: 'unknown',
            touch: 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0,
            screen: {
                width: window.screen?.width ?? 0,
                height: window.screen?.height ?? 0,
                pixelRatio: window.devicePixelRatio ?? 1,
            },
        };

        if (hasUACh) {
            try {
                // const uaData = nav.userAgentData as NavigatorUAData;
                const uaData = (nav.userAgentData ?? null) as UAData | null;
                const highEntropy = await uaData?.getHighEntropyValues?.([
                    'brands', 'platform', 'platformVersion', 'model', 'architecture', 'bitness', 'mobile',
                ]);

                browser = {
                    name: this.pickBrandName(highEntropy?.brands ?? uaData?.brands),
                    version: this.pickBrandVersion(highEntropy?.brands ?? uaData?.brands),
                    engine: this.inferEngineFromBrand(highEntropy?.brands ?? uaData?.brands),
                    brands: highEntropy?.brands ?? uaData?.brands,
                    mobile: highEntropy?.mobile ?? uaData?.mobile ?? false,
                };

                device = {
                    ...device,
                    os: highEntropy?.platform,
                    type: this.guessDeviceType({
                        mobile: browser.mobile ?? false,
                        width: device.screen?.width ?? 0,
                        height: device.screen?.height ?? 0,
                    }),
                };
            } catch {
                // fall back if UA-CH not allowed/available
            }
        }

        // Fallback to userAgent string parsing if still unknown
        if (browser.name === 'Unknown' || !browser.version) {
            const ua = navigator.userAgent || '';
            browser = { ...browser, ...this.parseUserAgentBrowser(ua) };
            device = { ...device, ...this.parseUserAgentDevice(ua, device) };
        }

        // locale/platform (non-sensitive)
        const locale = navigator.language;
        const platform = (navigator as any).platform;


        // NEW: set in-app browser flag
        const ua = navigator.userAgent || '';
        const inApp = this.detectInAppBrowser(ua);
        (browser as BrowserInfo).inAppBrowser = inApp

        this.cached = {
            browser,
            device,
            locale,
            platform,
            timestamp: new Date().toISOString(),
            isInApp: inApp
        };
        return this.cached;
    }

    getCached(): ClientInfo | undefined {
        return this.cached;
    }

    // --- helpers ---


    /** Detect well-known in-app browsers by UA tokens */
    private detectInAppBrowser(userAgent: string): boolean {
        if (!userAgent) return false;
        const ua = userAgent.toLowerCase();

        const patterns = [
            // Facebook family
            'fban', 'fbav', 'fbios', 'fb_iab', 'fb4a', 'messenger',
            // Instagram
            'instagram',
            // Google Search App (GSA)
            'gsa/', 'googleapp',
            // LinkedIn
            'linkedin',
            // WeChat
            'micromessenger', 'wechat',
            // LINE
            'line',
            // WhatsApp
            'whatsapp', 'wawebview',
            // Twitter / X
            'twitter', 'x-client',
            // Others
            'pinterest', 'snapchat', 'tiktok',
            'kakaotalk', 'kakaostory'
        ];

        return patterns.some(p => ua.includes(p));
    }


    private pickBrandName(brands?: { brand: string; version: string }[]): string {
        if (!brands?.length) return 'Unknown';
        // prioritize major brands
        const order = ['Chromium', 'Google Chrome', 'Microsoft Edge', 'Opera', 'Brave', 'Safari', 'Firefox'];
        for (const pref of order) {
            const hit = brands.find(b => b.brand.toLowerCase().includes(pref.toLowerCase()));
            if (hit) return pref;
        }
        // otherwise first non-grease brand
        const first = brands.find(b => !b.brand.includes('Not') && !b.brand.includes('Grease'));
        return first?.brand ?? 'Unknown';
    }

    private pickBrandVersion(brands?: { brand: string; version: string }[]): string | undefined {
        if (!brands?.length) return undefined;
        const primary = this.pickBrandName(brands);
        const record = brands.find(b => b.brand.toLowerCase().includes(primary.toLowerCase()));
        return record?.version;
    }

    private inferEngineFromBrand(brands?: { brand: string; version: string }[]): string | undefined {
        if (!brands?.length) return undefined;
        const s = brands.map(b => b.brand.toLowerCase()).join(' ');
        if (s.includes('chrome') || s.includes('chromium') || s.includes('edge') || s.includes('opera') || s.includes('brave')) {
            return 'Blink';
        }
        if (s.includes('safari') && !s.includes('chrome')) return 'WebKit';
        if (s.includes('firefox')) return 'Gecko';
        return undefined;
    }

    private guessDeviceType(opts: { mobile: boolean; width: number; height: number }): DeviceType {
        if (opts.mobile) return 'mobile';
        const min = Math.min(opts.width, opts.height);
        if (min > 600 && min < 1024) return 'tablet';
        if (min >= 1024) return 'desktop';
        return 'unknown';
    }

    private parseUserAgentBrowser(ua: string): Partial<BrowserInfo> {
        const lower = ua.toLowerCase();

        // Order matters: detect Edge/Opera before Chrome, and Chrome before Safari
        if (lower.includes('edg/')) {
            return { name: 'Edge', version: this.pickVersion(ua, /Edg\/([\d.]+)/), engine: 'Blink' };
        }
        if (lower.includes('opr/') || lower.includes('opera')) {
            return { name: 'Opera', version: this.pickVersion(ua, /OPR\/([\d.]+)/), engine: 'Blink' };
        }
        if (lower.includes('chrome/')) {
            return { name: 'Chrome', version: this.pickVersion(ua, /Chrome\/([\d.]+)/), engine: 'Blink' };
        }
        if (lower.includes('safari/') && lower.includes('version/')) {
            return { name: 'Safari', version: this.pickVersion(ua, /Version\/([\d.]+)/), engine: 'WebKit' };
        }
        if (lower.includes('firefox/')) {
            return { name: 'Firefox', version: this.pickVersion(ua, /Firefox\/([\d.]+)/), engine: 'Gecko' };
        }
        return { name: 'Unknown' };
    }

    private parseUserAgentDevice(ua: string, current: DeviceInfo): Partial<DeviceInfo> {
        const lower = ua.toLowerCase();
        const out: Partial<DeviceInfo> = { ...current };

        if (/android/.test(lower)) {
            out.os = 'Android';
            out.type = /mobile/.test(lower) ? 'mobile' : 'tablet';
        } else if (/iphone|ipod/.test(lower)) {
            out.os = 'iOS';
            out.type = 'mobile';
        } else if (/ipad/.test(lower)) {
            out.os = 'iPadOS';
            out.type = 'tablet';
        } else if (/windows/.test(lower)) {
            out.os = 'Windows';
            out.type = 'desktop';
        } else if (/mac os x|macintosh/.test(lower)) {
            out.os = 'macOS';
            out.type = 'desktop';
        } else if (/linux/.test(lower)) {
            out.os = 'Linux';
            out.type = 'desktop';
        }

        // Heuristic if still unknown
        if (out.type === 'unknown') {
            const min = Math.min(current.screen?.width ?? 0, current.screen?.height ?? 0);
            if (min && min < 600) out.type = 'mobile';
            else if (min && min < 1024) out.type = 'tablet';
            else if (min >= 1024) out.type = 'desktop';
        }

        return out;
    }

    private pickVersion(ua: string, re: RegExp): string | undefined {
        const m = ua.match(re);
        return m?.[1];
    }
}